import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Router} from '@angular/router';
import {finalize, forkJoin} from 'rxjs';
import {TranslocoService} from '@ngneat/transloco';
import {
  ApplicationFormSettings,
  Competitor,
  CompetitorSaveControlPointResult,
  CompetitorSaveControlPointResultStatus,
  CompetitorStatus,
  ConfirmPayment,
  ControlPointTime,
  EventItem,
  EventPaymentMethod,
  Gender,
  Lookups,
  ManageCompetitorErrorCodes, PaymentStatus,
  Race,
  RaceSettings,
  RaceStartType, RegistrationStatus,
  TeamType,
  WaypointType,
} from '@model';
import {
  AcceptCompetitorRegistrationComponent,
  CompetitorTableAction,
  CompetitorTableRowData,
  CompetitorTableType,
  ConfirmationModalComponent,
  EditCompetitorMeasurementTimes,
  EditCompetitorRaceComponent
} from '@components';
import {
  CompetitorService,
  LoaderService,
  MeasurementsService,
  RaceRegistrationService,
  TOAST_STATE,
  ToastService
} from '@services';
import {Sort} from '@utility';
import {ORGANISER_RACE_TEAM_MANAGEMENT} from "../../app-routing.module";
import {TeamMembersManagementComponent} from "@pages";

@Component({
  selector: 'app-competitor-table-dropdown-actions-row',
  templateUrl: './competitor-table-dropdown-actions-row.component.html',
  styleUrls: ['./competitor-table-dropdown-actions-row.component.scss']
})
export class CompetitorTableDropdownActionsRowComponent implements OnInit {
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() competitor?: Competitor;
  @Input() competitorRowData?: CompetitorTableRowData;
  @Input() index?: number;
  @Input() measurements = false;
  @Input() isTeamMembersManagement = false;
  @Input() competitors: Competitor[] = [];
  @Input() tableMode: CompetitorTableType = CompetitorTableType.RACE_VIEW;
  @Input() raceSettings?: RaceSettings;
  @Input() raceSettingsList: RaceSettings[] = [];
  @Input() applicationFormSettingsList: ApplicationFormSettings[] = [];
  @Input() applicationFormSettings?: ApplicationFormSettings;
  @Input() race?: Race;
  @Input() event?: EventItem;
  @Output() onEditTeamMemberButtonClick = new EventEmitter<OnEditCompetitor>();
  @Output() chronometarRaceCompetitorStartStop = new EventEmitter<Race>();
  @Output() onEditCompetitorEventEmitter = new EventEmitter<OnEditCompetitor>();
  @Output() onDeleteCompetitorEventEmitter = new EventEmitter<void>();

  rowDropdownActions: CompetitorTableAction[] = [];

  constructor(
    private translocoService: TranslocoService,
    private competitorService: CompetitorService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private router: Router,
    private raceRegistrationService: RaceRegistrationService,
    private measurementsService: MeasurementsService,
  ) {
  }

  ngOnInit(): void {
    if (!this.raceSettings && this.raceSettingsList && this.raceSettingsList.length > 0) {
      this.raceSettings = this.raceSettingsList.find(rs => rs.raceId === this.competitor?.raceId);
    }
    this.race = this.getRace(this.competitor!);
    this.initRowDropdownActions();
  }

  private getRace(competitor: Competitor) {
    return this.race ?? this.event?.racesList?.find(race => race.id === competitor.raceId);
  }

  private initRowDropdownActions = () => {
    this.rowDropdownActions = this.isTeamMembersManagement
      ? [CompetitorTableAction.EDIT_COMPETITOR]
      : this.measurements
        ? this.getMeasurementActions()
        : this.getDefaultActions();

    Sort.sortByOrder(this.rowDropdownActions, [
      CompetitorTableAction.START,
      CompetitorTableAction.CANCEL_START,
      CompetitorTableAction.SET_COMPETITOR_START_TIME,
      CompetitorTableAction.SET_COMPETITOR_DNS,
      CompetitorTableAction.SET_COMPETITOR_BIB,
      CompetitorTableAction.SET_COMPETITOR_RDY,
      CompetitorTableAction.EDIT_COMPETITOR,
      CompetitorTableAction.CONFIRM_REGISTRATION,
      CompetitorTableAction.ASSIGN_START_NUMBER,
      CompetitorTableAction.CHANGE_COMPETITOR_RACE,
      CompetitorTableAction.DELETE_COMPETITOR
    ]);
  }

  private getMeasurementActions = () => [
    CompetitorTableAction.START,
    CompetitorTableAction.CANCEL_START,
    CompetitorTableAction.SET_COMPETITOR_START_TIME,
    CompetitorTableAction.SET_COMPETITOR_BIB,
    CompetitorTableAction.SET_COMPETITOR_RDY,
    CompetitorTableAction.SET_COMPETITOR_DNS,
    CompetitorTableAction.EDIT_COMPETITOR,
    CompetitorTableAction.ASSIGN_START_NUMBER,
    CompetitorTableAction.CHANGE_COMPETITOR_RACE,
    CompetitorTableAction.DELETE_COMPETITOR
  ].filter(action => this.shouldIncludeAction(action, true));

  private getDefaultActions = () => [
    CompetitorTableAction.SET_COMPETITOR_BIB,
    CompetitorTableAction.SET_COMPETITOR_RDY,
    CompetitorTableAction.SET_COMPETITOR_DNS,
    CompetitorTableAction.EDIT_COMPETITOR,
    CompetitorTableAction.ASSIGN_START_NUMBER,
    CompetitorTableAction.CHANGE_COMPETITOR_RACE,
    CompetitorTableAction.CONFIRM_REGISTRATION,
    CompetitorTableAction.DELETE_COMPETITOR
  ].filter(action => this.shouldIncludeAction(action, false));

  private shouldIncludeAction = (action: CompetitorTableAction, measurement: boolean): boolean => {
    if (this.competitor?.status === CompetitorStatus.RMP && action !== CompetitorTableAction.DELETE_COMPETITOR) return false;
    if (this.competitor?.status === CompetitorStatus.RFP) {
     if (action === CompetitorTableAction.DELETE_COMPETITOR || action === CompetitorTableAction.EDIT_COMPETITOR) return true;
     else if (action === CompetitorTableAction.CONFIRM_REGISTRATION) return this.canAcceptRegistration();
     else return false;
    }
    if (measurement) {
      if (action === CompetitorTableAction.START) return !this.competitor?.startTime && (this.canStartCompetitor() || Race.isChronometerRace(this.race?.startEvery));
      if (action === CompetitorTableAction.CANCEL_START || action === CompetitorTableAction.SET_COMPETITOR_START_TIME) return !!this.competitor?.startTime;
      if (action === CompetitorTableAction.SET_COMPETITOR_BIB) return ![CompetitorStatus.BIB, CompetitorStatus.REG, CompetitorStatus.STA].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.SET_COMPETITOR_RDY) return ![CompetitorStatus.RDY, CompetitorStatus.REG, CompetitorStatus.STA].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.SET_COMPETITOR_DNS) return ![CompetitorStatus.DNS, CompetitorStatus.REG, CompetitorStatus.STA].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.DELETE_COMPETITOR && this.competitor?.startTime) return false;
    } else {
      if (action === CompetitorTableAction.SET_COMPETITOR_BIB) return ![CompetitorStatus.BIB, CompetitorStatus.REG].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.SET_COMPETITOR_RDY) return ![CompetitorStatus.RDY, CompetitorStatus.REG].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.SET_COMPETITOR_DNS) return ![CompetitorStatus.DNS, CompetitorStatus.REG].includes(this.competitor!.status!);
      if (action === CompetitorTableAction.DELETE_COMPETITOR && this.competitor?.startTime) return false;
      if (action === CompetitorTableAction.CONFIRM_REGISTRATION) return this.canAcceptRegistration();
    }
    return true;
  };

  private canAcceptRegistration = (): boolean => {
    if (this.measurements) return false;
    else if (this.event?.paymentSettings?.paymentMethod === EventPaymentMethod.TRACEY) return false;
    else {
      return !(this.competitor!.registration?.paymentStatus === PaymentStatus.SUCCESS || this.competitor!.registration?.registrationStatus === RegistrationStatus.CONFIRMED)
    }
  };

  private canStartCompetitor = (): boolean => {
    const race = this.event?.racesList?.find(r => r.id === this.competitor?.raceId);
    if (!this.raceSettings || !race) return false;

    const {blockStart, startByType} = this.raceSettings;
    const {startBlock, sex, startNumber} = this.competitor ?? {};

    if (!startNumber) {
      return false;
    }

    if (RaceSettings.isBlockRace(blockStart) && startBlock) {
      return race.raceStartItems?.some(item => item.blockNumber === startBlock && item.started) ?? false;
    }

    if (RaceSettings.isStartByGenderRace(startByType)) {
      const type = sex === Gender.MALE ? RaceStartType.MAN : RaceStartType.WOMAN;
      return race.raceStartItems?.some(item => item.type === type && item.started) ?? false;
    }

    return race.raceStartItems?.some(item => item.type === RaceStartType.RACE && item.started) ?? false;
  }

  handleDropdownAction(action: CompetitorTableAction, competitor: Competitor, index: number) {
    switch (action) {
      case CompetitorTableAction.START:
        this.startRaceForCompetitor(competitor);
        break;
      case CompetitorTableAction.CANCEL_START:
        this.stopRaceForCompetitor(competitor);
        break;
      case CompetitorTableAction.SET_COMPETITOR_DNS:
        this.manageCompetitorStatus(CompetitorStatus.DNS, competitor, index);
        break;
      case CompetitorTableAction.SET_COMPETITOR_BIB:
        this.manageCompetitorStatus(CompetitorStatus.BIB, competitor, index);
        break;
      case CompetitorTableAction.SET_COMPETITOR_RDY:
        this.manageCompetitorStatus(CompetitorStatus.RDY, competitor, index);
        break;
      case CompetitorTableAction.SET_COMPETITOR_START_TIME:
        this.editCompetitorStartTime(competitor);
        break;
      case CompetitorTableAction.EDIT_COMPETITOR:
        this.editCompetitor({index: this.index!, competitor: this.competitor!}, false);
        break;
      case CompetitorTableAction.ASSIGN_START_NUMBER:
        this.editCompetitor({index: this.index!, competitor: this.competitor!}, true);
        break;
      case CompetitorTableAction.CHANGE_COMPETITOR_RACE:
        this.changeCompetitorRace({index: this.index!, competitor: this.competitor!});
        break;
      case CompetitorTableAction.DELETE_COMPETITOR:
        this.openDeleteCompetitorDialog();
        break;
      case CompetitorTableAction.CONFIRM_REGISTRATION:
        this.openConfirmRegistrationDialog();
        break;
    }
  }

  startRaceForCompetitor(competitor: Competitor) {
    this.loaderService.startLoading();
    let startTime = new Date();
    competitor.controlPointsTimes = [{time: startTime}]
    this.measurementsService.startCompetitor(competitor, Race.isChronometerRace(this.race!.startEvery), () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('measurements.competitor.start.time.success'));
      competitor.startTime = startTime;
      competitor.status = CompetitorStatus.STA;
      if (Race.isChronometerRace(this.race!.startEvery)) {
        this.chronometarRaceCompetitorStartStop.emit(this.race);
      }
      this.onEditCompetitorEventEmitter.emit({competitor: competitor, index: this.index!});
      this.initRowDropdownActions();
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('measurements.competitor.start.time.error'));
    });
  }

  stopRaceForCompetitor(competitor: Competitor) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {centered: true});
    modalRef.componentInstance.text = this.translocoService.translate('measurements.stop.race.competitor.text', {
      name: `${competitor.firstName ?? ''} ${competitor.lastName ?? ''}`
    });
    modalRef.componentInstance.title = this.translocoService.translate('measurements.stop.competitor');
    modalRef.result.then(() => {
        this.loaderService.startLoading();
        this.measurementsService.stopCompetitor(competitor.id!,this.race!.id!, Race.isChronometerRace(this.race?.startEvery), () => {
          competitor.startTime = undefined;
          competitor.status = CompetitorStatus.RDY;
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('measurements.competitor.stop.time.success'));
          if (Race.isChronometerRace(this.race!.startEvery)) {
            this.chronometarRaceCompetitorStartStop.emit(this.race);
          }
          this.onEditCompetitorEventEmitter.emit({competitor: competitor, index:this.index!});
          this.initRowDropdownActions();
        }, () => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('measurements.competitor.stop.time.error'));
        });
      },
      () => {
      }
    );
  }

  editCompetitorStartTime(competitor: Competitor) {
    this.loaderService.startLoading();
    if (RaceSettings.isRoundRace(this.raceSettings?.minimumLapDuration)) {
      this.measurementsService
        .findLapsTimes(competitor.id!, this.competitor!.raceId!)
        .pipe(finalize(() => this.loaderService.dismissLoading()))
        .subscribe(controlPointTimes => {
          competitor.controlPointsTimes = controlPointTimes;
          this.openEditCompetitorStartTimeModal(competitor, true);
        });
    } else {
      this.loaderService.startLoading();
      this.measurementsService
        .findControlPointTimes(competitor.id!, this.competitor!.raceId!)
        .pipe(finalize(() => this.loaderService.dismissLoading()))
        .subscribe(controlPointTimes => {
          competitor.controlPointsTimes = controlPointTimes;
          this.openEditCompetitorStartTimeModal(competitor);
        });
    }
  }

  openEditCompetitorStartTimeModal(competitor: Competitor, roundRace: boolean = false) {
    const modalRef: NgbModalRef = this.modalService.open(EditCompetitorMeasurementTimes, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.title = this.translocoService.translate('measurements.competitor.edit.time.title');
    modalRef.componentInstance.competitor = competitor;
    modalRef.componentInstance.roundRace = roundRace;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.raceSettings = this.raceSettingsList.find(settings => settings.raceId === competitor.raceId);
    modalRef.componentInstance.onSave.subscribe((competitor: Competitor) => {
      const handleSaveSuccess = (competitorResults: CompetitorSaveControlPointResult[]) => {
        this.loaderService.dismissLoading();
        const allOk = competitorResults.every(result => result.status === CompetitorSaveControlPointResultStatus.OK);
        if (allOk) {
          modalRef.close();
          competitor.startTime = competitor.controlPointsTimes?.find(controlPointTime => controlPointTime.controlWaypoint?.waypointTypeId === WaypointType.START_WAYPOINT)?.time;
          this.onEditCompetitorEventEmitter.emit({competitor: competitor, index: this.index!});
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('measurements.competitor.edit.time.success'));
        }
        else {
          const saveResult: Map<number, CompetitorSaveControlPointResultStatus> = new Map();
          competitorResults.forEach((result: CompetitorSaveControlPointResult)=>{
            result.competitor?.controlPointsTimes?.forEach((controlPointTime: ControlPointTime)=>{
              if (controlPointTime.id) {
                saveResult.set(controlPointTime.id!, result.status)
              }
            })
          })
          modalRef.componentInstance.saveResult = [...saveResult];
        }
      }
      const handleSaveError = () => {
        this.loaderService.dismissLoading()
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('measurements.competitor.edit.time.error'));
      }

      this.loaderService.startLoading();
      roundRace ?
        this.measurementsService.saveOrUpdateCompetitorLaps(competitor, this.event!.id!, handleSaveSuccess, handleSaveError)
        : this.measurementsService.saveOrUpdateCompetitorControlPoints(competitor, this.event!.id!, handleSaveSuccess, handleSaveError)
    });
  }

  manageCompetitorStatus(status: CompetitorStatus, competitor: Competitor, index: number) {
    const competitorCopy: Competitor = {...competitor, status};
    this.competitorService.manageCompetitorStatus(
      competitorCopy,
      () => {
        this.loaderService.dismissLoading();
        this.competitors[index] = competitorCopy;
        this.onEditCompetitorEventEmitter.emit({competitor: competitorCopy, index:index});
        this.initRowDropdownActions();
      },
      errorCode => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(`competitor.edit.error.${errorCode}`));
      }
    );
  }

  changeCompetitorRace(onEditCompetitor: OnEditCompetitor) {
    const {competitor, index} = onEditCompetitor;
    const modalRef: NgbModalRef = this.modalService.open(EditCompetitorRaceComponent, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.title = this.translocoService.translate('competitor.table.dropdown.action.10');
    modalRef.componentInstance.competitor = competitor;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.races = this.event?.racesList ? [...this.event!.racesList] : []
    modalRef.componentInstance.onSave.subscribe((competitorCopy: Competitor) => {
      const handleSaveSuccess = (updatedCompetitor: Competitor) => {
       modalRef.close();
        this.competitors[index] = updatedCompetitor;
        this.onEditCompetitorEventEmitter.emit({competitor: updatedCompetitor, index: index});
       this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('measurements.competitor.edit.race.success'));
      }
      const handleSaveError = (errorCode?: ManageCompetitorErrorCodes | undefined) => {
        modalRef.componentInstance.displayError(errorCode)
      }
      this.competitorService.manageCompetitorRace(competitorCopy, handleSaveSuccess, handleSaveError)
    });
  }

  editCompetitor(onEditCompetitor: OnEditCompetitor, assignStartNumber: boolean) {
    const {competitor, index} = onEditCompetitor;
    this.onEditTeamMemberButtonClick.emit(onEditCompetitor);
    if (this.raceSettings?.teamType === TeamType.SINGLE && competitor.id || assignStartNumber) {
      this.loaderService.startLoading();
      forkJoin([
        this.competitorService.fetchCompetitorById(competitor.id!),
        this.raceRegistrationService.findRegistrationByCompetitorID(competitor.id!)
      ]).subscribe({
        next: ([competitorData, raceRegistrationData]) => {
          this.loaderService.dismissLoading();
          const modalRef: NgbModalRef = this.modalService.open(TeamMembersManagementComponent, {
            size: 'lg',
            scrollable: true
          });
          modalRef.componentInstance.competitor = competitorData;
          modalRef.componentInstance.raceSettings = competitorData ? this.raceSettings : undefined;
          modalRef.componentInstance.applicationFormSettings = competitorData ? this.getApplicationFormSettings(competitorData) : undefined;
          modalRef.componentInstance.raceRegistration = raceRegistrationData;
          modalRef.componentInstance.title = 'competitor.edit';
          modalRef.componentInstance.race = this.race;
          modalRef.componentInstance.eventItem = this.event;
          modalRef.componentInstance.assignStartNumber = assignStartNumber;
          modalRef.componentInstance.onSaveSuccessAction.subscribe((competitor: Competitor) => {
            this.competitors[index] = competitor;
            this.onEditCompetitorEventEmitter.emit({competitor: competitor, index:index});
            this.initRowDropdownActions();
          });
          modalRef.result.then(
            () => {
            },
            () => {
            }
          );
        },
        error: () => this.loaderService.dismissLoading()
      });
    } else if (this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW) {
      this.router.navigate([ORGANISER_RACE_TEAM_MANAGEMENT], {
        queryParams: {raceId: competitor.raceId, competitorId: competitor.id},
        queryParamsHandling: 'merge'
      });
    }
  }

  private getApplicationFormSettings = (competitor: Competitor): ApplicationFormSettings | undefined =>
    this.tableMode === CompetitorTableType.EVENT_VIEW
      ? this.applicationFormSettingsList.find(appSettings => appSettings.raceId === competitor.raceId)
      : this.applicationFormSettings;

  openDeleteCompetitorDialog() {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("race.management.competitors.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("race.management.competitors.delete.dialog.text");

    modalRef.result.then((_) => {
      this.onDeleteCompetitor();
    }, (_) => {
      /// ignore
    });
  }

  onDeleteCompetitor(): void {
    this.loaderService.startLoading()
    this.competitorService.deleteCompetitor(this.competitor!.id!).subscribe((result: boolean) => {
      if (result) {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.management.competitors.delete.success"));
        this.onDeleteCompetitorEventEmitter.emit();
      } else {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.management.competitors.delete.error"));
      }
    })
  }

  openConfirmRegistrationDialog() {
    const modalRef: NgbModalRef = this.modalService.open(AcceptCompetitorRegistrationComponent, {
      centered: true,
      size: "lg"
    });
    modalRef.componentInstance.competitor = this.competitor;
    modalRef.componentInstance.onSave.subscribe((confirmPayment: ConfirmPayment) => {
      this.onConfirmRegistration(confirmPayment);
    });
  }

  onConfirmRegistration(confirmPayment: ConfirmPayment): void {
    this.loaderService.startLoading()

    const onSuccess = () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.management.competitors.confirm.registration.success"));
      forkJoin([
        this.competitorService.fetchCompetitorById(this.competitor!.id!),
        this.raceRegistrationService.findRegistrationByCompetitorID(this.competitor!.id!)
      ]).subscribe(([competitorData, raceRegistrationData]) => {
        this.competitor = { ...competitorData, registration: { ...raceRegistrationData } };
        this.competitors[this.index!] = { ...this.competitor };
        this.onEditCompetitorEventEmitter.emit({ competitor: this.competitor!, index: this.index! });
        this.initRowDropdownActions();
      });
    }
    const onError = () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.management.competitors.confirm.registration.error"));
    }
    this.raceRegistrationService.confirmRegistration(confirmPayment, onSuccess, onError);
  }


  isNumber = (value: number | undefined) => typeof value === 'number';
}

export interface OnEditCompetitor {
  competitor: Competitor;
  index: number;
}
